import { Injectable } from '@angular/core';
import { SheetLevelStatusMap } from './filing-form-state.service';

/**
 * Shape persisted to localStorage per conversion. The `complete` flag marks
 * whether the cached statuses came from a fully finished validation cycle
 * (true) or a mid-cycle incremental save (false). Listing/dashboard consumers
 * should treat `complete: false` as a partial result that may still resume.
 */
export interface ConversionValidationCacheEntry {
  version: number;
  complete: boolean;
  statuses: SheetLevelStatusMap;
}

/**
 * ConversionValidationCacheService
 *
 * Single source of truth for per-conversion sheet-level validation statuses
 * persisted to localStorage. Each conversion is stored as its own entry under
 * the key `mbrs-validation-cache-<conversionId>` (matches the legacy key
 * format used by FilingFormStateService so existing entries are reused
 * verbatim, no migration required).
 *
 * Why this service exists separately from FilingFormStateService:
 *   FilingFormStateService is scoped to the *currently open* conversion and
 *   reads/writes the cache implicitly via its own `currentConversionId`. The
 *   listing and dashboard pages need to read, write, and invalidate the cache
 *   for *any* conversion (typically 15 paginated rows at once) without
 *   touching the editor's in-memory state. This service exposes that surface
 *   with an explicit `conversionId` argument on every method.
 *
 * Invalidation triggers (wired by other services in Phase 1):
 *   - FilingFormStateService submit success      -> invalidate(conversionId)
 *   - AuthService.logout()                       -> clearAll()
 *   - ConversionProgressService SignalR update   -> invalidate(conversionId)
 *     (when status flips from InProgress to Review/Success)
 */
@Injectable({ providedIn: 'root' })
export class ConversionValidationCacheService {
  private readonly STORAGE_KEY_PREFIX = 'mbrs-validation-cache';
  /**
   * Bump this whenever a validator logic change can make previously-cached
   * statuses wrong. `get()` discards (and removes) any entry whose version
   * does not match, forcing a fresh recompute through the current validators.
   *
   * v1 -> v2: ConversionValidatorService.buildContext() now resolves the
   *   active filing-info slice by real content (TypeOfSubmission) instead of
   *   reading the `filingInfo` slot blindly. Before the fix, Phase 2
   *   conversions cached a false `hasErrors: true` for visibility-aware
   *   sheets (notably role 8 / statementOfFinancialPosition) because the
   *   validators saw the 16-key `filingPrerequisites` stub, found no
   *   visibility fields, and treated all columns as visible. Discarding v1
   *   entries clears those stale ✗.
   *
   * v2 -> v3: Phase-1 conversions are now validated against only
   *   `filingPrerequisites` (the sole sheet revealed in Phase 1) instead of
   *   the full 26-sheet registry. Before the fix, opening or listing a
   *   Phase-1 conversion cached a false `hasErrors: true` for all 25
   *   not-yet-converted downstream sheets, surfacing the row as "Review" even
   *   when Filing Prerequisites was clean. Discarding v2 entries clears those
   *   poisoned all-26 Phase-1 payloads so the next read recomputes the scoped
   *   result. See PHASE1_SHEET_KEYS in conversion-validator.service.ts.
   *
   * v3 -> v4: Role 6 (directorBusinessReview) field
   *   `ssmt:DisclosureOfOtherBusinessReviewApplied` is now always optional (its
   *   conditional-required rule under `ssmt:Others === "Yes"` was removed —
   *   see role6-director-business-review.validator.ts). A conversion cached
   *   under v3 with Others="Yes" and that field empty holds a stale
   *   `hasErrors: true` for role 6 that no longer applies. Discarding v3 entries
   *   forces a recompute so the listing/dashboard shows the corrected
   *   (non-blocking) role-6 status immediately instead of waiting for a
   *   submit/logout/SignalR invalidation.
   *
   * v4 -> v5: entries written before the validation-cache payload fix carry a
   *   pessimistic `hasErrors: true` for sheets no validator ever ran — every
   *   sheet is seeded that way in `initialSheetLevelStatus`, and the debounced
   *   persist used to publish the whole in-memory map rather than only the
   *   sheets that reported. Those phantoms were inert while the same write
   *   forced `complete: false` (the listing reads the cache only when
   *   `complete`), but the flag is now honest, and
   *   `ConversionStatusResolverService.deriveTerminal` is
   *   `some(hasErrors) -> Review` over every key — with `resolve()` POSTing that
   *   verdict back to the backend. Discarding v4 entries guarantees no
   *   pre-fix payload can be read under the new flag. In-app navigation out of
   *   the filing form already calls `clearAll()` via
   *   `CurrentSheetCacheService.flushConversionCaches`, so most entries would
   *   have turned over anyway; this makes it deterministic rather than likely.
   */
  private readonly CACHE_VERSION = 5;

  private storageKey(conversionId: string): string {
    return `${this.STORAGE_KEY_PREFIX}-${conversionId}`;
  }

  /**
   * Read the cached entry for a conversion. Returns null on cache miss or
   * version mismatch (a stale-version entry is removed eagerly so subsequent
   * reads stay cheap).
   */
  get(conversionId: string): ConversionValidationCacheEntry | null {
    try {
      const stored = localStorage.getItem(this.storageKey(conversionId));
      if (!stored) return null;

      const parsed = JSON.parse(stored);
      if (!parsed || parsed.version !== this.CACHE_VERSION) {
        localStorage.removeItem(this.storageKey(conversionId));
        return null;
      }

      return {
        version: parsed.version,
        // Older entries written before the incremental-save feature shipped
        // omit `complete`. Treat them as fully complete for compatibility.
        complete: parsed.complete !== false,
        statuses: parsed.statuses as SheetLevelStatusMap,
      };
    } catch (error) {
      console.error('[ConversionValidationCache] get failed:', error);
      return null;
    }
  }

  /**
   * Write the validation statuses for a conversion. Pass `complete: true`
   * after a full cycle finishes; pass `false` for incremental mid-cycle
   * saves.
   */
  set(conversionId: string, statuses: SheetLevelStatusMap, complete: boolean = true): void {
    try {
      const entry: ConversionValidationCacheEntry = {
        version: this.CACHE_VERSION,
        complete,
        statuses,
      };
      localStorage.setItem(this.storageKey(conversionId), JSON.stringify(entry));
    } catch (error) {
      console.error('[ConversionValidationCache] set failed:', error);
    }
  }

  /**
   * Remove a single sheet's entry from a conversion's cached statuses.
   * Flips `complete` to false so consumers know a partial result remains
   * and the dropped sheet should be re-validated. No-op on cache miss.
   */
  invalidateSheet(conversionId: string, sheetKey: keyof SheetLevelStatusMap): void {
    try {
      const key = this.storageKey(conversionId);
      const stored = localStorage.getItem(key);
      if (!stored) return;

      const parsed = JSON.parse(stored);
      if (parsed?.statuses && parsed.statuses[sheetKey]) {
        delete parsed.statuses[sheetKey];
        parsed.complete = false;
        localStorage.setItem(key, JSON.stringify(parsed));
      }
    } catch (error) {
      console.error('[ConversionValidationCache] invalidateSheet failed:', error);
    }
  }

  /**
   * Drop the entire cache entry for one conversion. Called on submit success
   * and on SignalR status flips (backend payload is outdated, so we discard
   * any cached client status and let the next visit re-validate).
   */
  invalidate(conversionId: string): void {
    try {
      localStorage.removeItem(this.storageKey(conversionId));
    } catch (error) {
      console.error('[ConversionValidationCache] invalidate failed:', error);
    }
  }

  /**
   * Drop every cache entry across all conversions. Called on logout so a
   * different user starting the next session never sees stale results.
   */
  clearAll(): void {
    try {
      const keysToRemove: string[] = [];
      for (let storageIndex = 0; storageIndex < localStorage.length; storageIndex++) {
        const key = localStorage.key(storageIndex);
        if (key && key.startsWith(`${this.STORAGE_KEY_PREFIX}-`)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.error('[ConversionValidationCache] clearAll failed:', error);
    }
  }
}
